/**
 * /companies/:companyId/archivos — the company document store (PF-347):
 * soportes / archivos digitales. Upload (base64-in-JSON against the BFF),
 * filtered list, download, rename/edit metadata, and delete. Entity-linked
 * documents (soportes attached to a factura/compra/tercero…) show their link;
 * attaching from the entity detail pages is a follow-up.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Download,
  FileText,
  FolderOpen,
  Loader2,
  Pencil,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getDocumentContentBlob, type CompanyDocument } from "@/lib/api/documents";
import {
  useDeleteDocument,
  useDocuments,
  useUpdateDocument,
  useUploadDocument,
} from "@/lib/api/documents.queries";
import {
  ACCEPT_ATTR,
  ENTITY_LABEL,
  MAX_UPLOAD_BYTES,
  formatBytes,
  formatDocumentDate,
  resolveContentType,
} from "@/lib/document-files";
import { useActiveCompany } from "@/lib/company-context";

export const Route = createFileRoute("/companies/$companyId/archivos/")({
  component: ArchivosPage,
});

function ArchivosPage() {
  return (
    <RequireAuth>
      <AppShell>
        <ArchivosContent />
      </AppShell>
    </RequireAuth>
  );
}

function ArchivosContent() {
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // ── list filters ───────────────────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const listParams = useMemo(
    () => ({ q: search || undefined, page, pageSize: 50 }),
    [search, page],
  );
  const documentsQuery = useDocuments(companyId, listParams);

  // ── upload ─────────────────────────────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploadCategory, setUploadCategory] = useState("");
  const [uploadNotes, setUploadNotes] = useState("");
  const uploadMutation = useUploadDocument(companyId);

  function onFilePicked(file: File | null) {
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error(`El archivo supera el máximo de 10 MB (${formatBytes(file.size)}).`);
      return;
    }
    if (!resolveContentType(file)) {
      toast.error("Tipo de archivo no soportado.");
      return;
    }
    setPendingFile(file);
  }

  function submitUpload() {
    const file = pendingFile;
    if (!file) return;
    const contentType = resolveContentType(file);
    if (!contentType) return;
    const reader = new FileReader();
    reader.onload = () => {
      uploadMutation.mutate(
        {
          fileName: file.name,
          contentType,
          contentBase64: String(reader.result ?? ""),
          category: uploadCategory.trim() || null,
          notes: uploadNotes.trim() || null,
        },
        {
          onSuccess: (doc) => {
            toast.success(`"${doc.fileName}" subido.`);
            setPendingFile(null);
            setUploadCategory("");
            setUploadNotes("");
            if (fileInputRef.current) fileInputRef.current.value = "";
          },
          onError: (err) => toast.error(err.message),
        },
      );
    };
    reader.onerror = () => toast.error("No se pudo leer el archivo.");
    reader.readAsDataURL(file);
  }

  // ── row actions ────────────────────────────────────────────────────────────
  const updateMutation = useUpdateDocument(companyId);
  const deleteMutation = useDeleteDocument(companyId);
  const [editing, setEditing] = useState<CompanyDocument | null>(null);
  const [editName, setEditName] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  function openEdit(doc: CompanyDocument) {
    setEditing(doc);
    setEditName(doc.fileName);
    setEditCategory(doc.category ?? "");
    setEditNotes(doc.notes ?? "");
  }

  function submitEdit() {
    if (!editing) return;
    updateMutation.mutate(
      {
        documentId: editing.id,
        input: {
          fileName: editName.trim() || editing.fileName,
          category: editCategory.trim() || null,
          notes: editNotes.trim() || null,
        },
      },
      {
        onSuccess: () => {
          toast.success("Documento actualizado.");
          setEditing(null);
        },
        onError: (err) => toast.error(err.message),
      },
    );
  }

  async function downloadDocument(doc: CompanyDocument) {
    setDownloadingId(doc.id);
    try {
      const blob = await getDocumentContentBlob(companyId, doc.id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = doc.fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo descargar el archivo.");
    } finally {
      setDownloadingId(null);
    }
  }

  function removeDocument(doc: CompanyDocument) {
    if (!window.confirm(`¿Eliminar "${doc.fileName}"? El archivo se borra del almacenamiento.`)) {
      return;
    }
    deleteMutation.mutate(doc.id, {
      onSuccess: () => toast.success("Documento eliminado."),
      onError: (err) => toast.error(err.message),
    });
  }

  const data = documentsQuery.data;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <FolderOpen className="h-5 w-5" />
            Archivos
          </h1>
          <p className="text-sm text-muted-foreground">
            Soportes y documentos digitales de la empresa: RUT, contratos, extractos, el
            soporte detrás de un gasto…
          </p>
        </div>
      </div>

      {/* ── Upload card ─────────────────────────────────────────────────── */}
      <Card>
        <CardContent className="flex flex-col gap-3 pt-6">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="archivo-file">Archivo (máx. 10 MB)</Label>
              <Input
                id="archivo-file"
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_ATTR}
                className="w-72"
                onChange={(e) => onFilePicked(e.target.files?.[0] ?? null)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="archivo-category">Categoría (opcional)</Label>
              <Input
                id="archivo-category"
                placeholder="rut, contrato, soporte…"
                className="w-48"
                value={uploadCategory}
                onChange={(e) => setUploadCategory(e.target.value)}
              />
            </div>
            <div className="flex min-w-56 flex-1 flex-col gap-1.5">
              <Label htmlFor="archivo-notes">Notas (opcional)</Label>
              <Input
                id="archivo-notes"
                placeholder="Descripción corta"
                value={uploadNotes}
                onChange={(e) => setUploadNotes(e.target.value)}
              />
            </div>
            <Button
              onClick={submitUpload}
              disabled={!pendingFile || uploadMutation.isPending}
            >
              {uploadMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-2 h-4 w-4" />
              )}
              Subir
            </Button>
          </div>
          {pendingFile ? (
            <p className="text-xs text-muted-foreground">
              Seleccionado: {pendingFile.name} ({formatBytes(pendingFile.size)})
            </p>
          ) : null}
        </CardContent>
      </Card>

      {/* ── Filters ─────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Buscar por nombre…"
          className="w-64"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        {data ? (
          <span className="text-sm text-muted-foreground">
            {data.total} archivo{data.total === 1 ? "" : "s"}
          </span>
        ) : null}
      </div>

      {/* ── List ────────────────────────────────────────────────────────── */}
      {documentsQuery.isLoading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando archivos…
        </div>
      ) : documentsQuery.isError ? (
        <ErrorState
          title="No pudimos cargar los archivos"
          error={documentsQuery.error}
          onRetry={() => void documentsQuery.refetch()}
        />
      ) : !data || data.documents.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <FolderOpen className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">Aún no hay archivos</p>
            <p className="text-xs text-muted-foreground">
              Sube el RUT, contratos o cualquier soporte usando el formulario de arriba.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Categoría</TableHead>
                  <TableHead>Vinculado a</TableHead>
                  <TableHead className="text-right">Tamaño</TableHead>
                  <TableHead>Subido</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.documents.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{doc.fileName}</p>
                          {doc.notes ? (
                            <p className="truncate text-xs text-muted-foreground">{doc.notes}</p>
                          ) : null}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {doc.category ? <Badge variant="secondary">{doc.category}</Badge> : "—"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {doc.entityType
                        ? `${ENTITY_LABEL[doc.entityType] ?? doc.entityType}`
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {formatBytes(doc.sizeBytes)}
                    </TableCell>
                    <TableCell className="text-sm">{formatDocumentDate(doc.createdAt)}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Descargar"
                          disabled={downloadingId === doc.id}
                          onClick={() => void downloadDocument(doc)}
                        >
                          {downloadingId === doc.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Editar"
                          onClick={() => openEdit(doc)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Eliminar"
                          disabled={deleteMutation.isPending}
                          onClick={() => removeDocument(doc)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {totalPages > 1 ? (
              <div className="flex items-center justify-end gap-2 pt-3">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <span className="text-xs text-muted-foreground">
                  Página {page} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Siguiente
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* ── Edit dialog ─────────────────────────────────────────────────── */}
      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar documento</DialogTitle>
            <DialogDescription>
              Cambia el nombre, la categoría o las notas. El contenido del archivo es
              inmutable.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-name">Nombre</Label>
              <Input
                id="edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-category">Categoría</Label>
              <Input
                id="edit-category"
                placeholder="rut, contrato, soporte…"
                value={editCategory}
                onChange={(e) => setEditCategory(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-notes">Notas</Label>
              <Input
                id="edit-notes"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancelar
            </Button>
            <Button onClick={submitEdit} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
